import { ScreenManager } from "../../../managers/ScreenManager";
import { ToggleGroup, ToggleGroupEvents, ToggleGroupItemSelectedEventData } from "../../../ui/ToggleGroup";
import { ScreenOrientationType } from "../../../utils/GetOrientation";
import { RectSize } from "../../../utils/RectSize";
import { LoaderFileKeys } from "../../constant/AppConstants";
import { LayoutDataManager } from "../../managers/LayoutDataManager";
import { AppSettings } from "../../managers/AppSettings";
import { AppObjectPool } from "../../pooling/AppObjectPool";
import { OptionsLeafPopupContentBase } from "./OptionsLeafPopupContentBase";
import { OptionsLeafPopupImageItem } from "./OptionsLeafPopupImageItem";


export type OptionsLeafPopupImageItemsContentScaleItemProps = {
    /**
     * Scale content by aspect ratio
     * > linear increasing across orientations (good)
     * > provides consistent view across diff resolution but same AR screens
     */
    minAR?: number;
    maxAR?: number;
    itemCols: number;
}

/**
 * Content of popup containing list of selectable image items
 */
export class OptionsLeafPopupImageItemsContent
    extends OptionsLeafPopupContentBase {
    protected itemSpacingBase: RectSize = new RectSize(34, 34);
    protected itemSpacing: RectSize = new RectSize();
    // NOTE: toggleGroup contains items list
    protected toggleGroup: ToggleGroup;
    protected gridAlignConfig: Phaser.Types.Actions.GridAlignConfig;
    /**
     * Number of columns of items
     */
    protected itemCols: number;
    /**
     * Default width of item graphics. 
     * > Scaling parent ensures we can use fixed item width.
     */
    protected itemCellWidth: number;
    /**
     * Default height of item graphics.
     */
    protected itemCellHeight: number;
    /**
     * Assumed from scaleItemProps appropriate for current AspectRatio.
     * @see scaleItemsProps
     */
    protected itemScale: number;
    private itemPoolGroupKey: string = "OptionsLeafPopupImageItem";
    /**
     * Emulation of media queries; Based on current aspect ratio.
     */
    private scaleItemProps: OptionsLeafPopupImageItemsContentScaleItemProps[];


    public override Init(cullItems: boolean = true, onBtnBackClick: Function): void {
        super.Init(cullItems, onBtnBackClick);

        this.itemSpacing.CopyFrom(this.itemSpacingBase);
        this.toggleGroup = new ToggleGroup();
        this.gridAlignConfig = {};
        const itemLayoutData = LayoutDataManager.GetSharedData().options.leafPopup.imageItems.item;
        this.itemCellWidth = itemLayoutData.cellWidth;
        this.itemCellHeight = itemLayoutData.cellHeight;

        this.scaleItemProps = [];
        this.scaleItemProps.push({
            // minAR: 0.0,
            maxAR: 0.65,
            itemCols: 2,
        });
        this.scaleItemProps.push({
            minAR: 0.65,
            maxAR: 1.0,
            itemCols: 3,
        });
        // landscape
        this.scaleItemProps.push({
            minAR: 1.0,
            maxAR: 1.3,
            itemCols: 2,
        });
        // // 2.2+ for iPhone 12
        // // 2.3+ for Samsung Galaxy S20 Ultra
        // but even Android has bars.. so increased further.
        this.scaleItemProps.push({
            minAR: 1.3,
            // maxAR: 3.0,
            itemCols: 3,
        });
        // this.scaleItemProps.push({
        //     minAR: 3.0,
        //     itemCols: 4,
        // });
    }

    public override Build(): void {
        // > bg
        super.Build();

        // this.name = "OptionsLeafPopupImageItemsContent";

        // add items in @see SetData
    }

    /**
     * Data has been set. 
     * REBUILD view.
     * 
     * Exec order
     * > parent(OptionsLeafPopup)::SetData
     *   > this::SetData
     *     > parent
     */
    public override UpdateFromData(): void {
        // console.log("UpdateFromData");

        // > title
        super.UpdateFromData();

        // console.log("itemLayoutData=",this.itemLayoutData, "itemThemeData=",this.itemThemeData, "itemLocData=",this.itemLocData, "itemLiveData=",this.itemLiveData);

        const toggleGroupItemsCount = this.toggleGroup.GetLength();
        const newItemCount = this.itemThemeData.items.length;
        const selectItemIndex: number = this.itemLiveData.value as number;
        let item: OptionsLeafPopupImageItem;

        // need more items
        if (newItemCount > toggleGroupItemsCount) {
            for (let i: number = toggleGroupItemsCount; i < newItemCount; i++) {
                // get new/pooled item
                item = this.GetFromPoolOrNew();
                // add
                this.toggleGroup.AddItem(item);
                // this.add(item);
                this.content.add(item);
            }
        }
        else {
            if (newItemCount < toggleGroupItemsCount) {
                // pool extra items
                for (let i: number = newItemCount; i < toggleGroupItemsCount; i++) {
                    item = this.toggleGroup.RemoveLast() as OptionsLeafPopupImageItem;
                    // Phaser.Utils.Array.Remove(this.items, toggle);
                    this.remove(item);
                    item.SetSelected(false); // reset item (TODO: test)
                    AppObjectPool.DropObject(item, this.itemPoolGroupKey);
                }
            }
        }

        // set proper thumbnail views for all items (eg specific carb back image for each thumb)
        this.toggleGroup.GetItems().forEach((item: OptionsLeafPopupImageItem, i) => {
            // const itemText = this.itemLocData.choices[i].itemLabel as string;
            item.SetButtonTextureDatas({
                out: { texture: LoaderFileKeys.themes, frame: this.itemThemeData.items[i].preview.out }
            });
            item.SetSelectedTextureDatas({
                out: { texture: LoaderFileKeys.themes, frame: this.itemThemeData.items[i].preview.over }
            });
            item.UpdateView();
            // console.log(this.itemThemeData.items[i].preview.out, this.itemThemeData.items[i].preview.over);
        });

        // select proper item by index
        if (selectItemIndex < this.toggleGroup.GetLength()) {
            this.toggleGroup.SelectItemByIndex(selectItemIndex);
        }

        // UpdateFromLocalizationChange
        this.UpdateFromLocalizationChange();
    }

    public UpdateFromLocalizationChange(): void {

    }

    protected GetFromPoolOrNew(): OptionsLeafPopupImageItem {
        let item: OptionsLeafPopupImageItem = AppObjectPool.PickObject(this.itemPoolGroupKey);
        if (item == null) {
            item = new OptionsLeafPopupImageItem(this.scene);
            item.InitBase({
                out: { texture: LoaderFileKeys.themes, frame: "bg1-out.png" },
                // out:{texture:LoaderFileKeys.ui, frame:"results-listItem-bg.png"},
                // over:{texture:LoaderFileKeys.ui, frame:"results-listItem-bg.png"},
                // disabled:{texture:LoaderFileKeys.ui, frame:"results-listItem-bg.png"},
            }, true);
            item.InitToggle({
                out: { texture: LoaderFileKeys.themes, frame: "bg1-out.png" },
            }, false); // isSelected?
            // hack in TL origin of item
            // this pushes hitArea TL by a half, bring it back in @see PostInit
            item.view.setOrigin(0);
            // > UpdatePositions
            // > UpdateHitArea
            item.PostInit();
            // item.setData("baseScale", 0.8);
            // this.add(item); // outside
        }
        return item;
    }

    public override RegisterListeners(): void {
        super.RegisterListeners();
        this.toggleGroup.GetEventEmitter().on(ToggleGroupEvents.ITEM_SELECTED, this.OnListItemSelected, this);
    }

    public override UnregisterListeners(): void {
        super.UnregisterListeners();
        this.toggleGroup.GetEventEmitter().off(ToggleGroupEvents.ITEM_SELECTED, this.OnListItemSelected, this);
    }

    protected override OnListItemSelected(eventData: ToggleGroupItemSelectedEventData): void {
        // console.log("item selected:",eventData.itemIndex);

        // change live data index
        // const selectedItemIndex = this.toggleGroup.GetSelectedItemIndex();
        const selectedItemIndex = eventData.itemIndex;
        // CHANGE LIVE HERE?
        this.itemLiveData.value = selectedItemIndex;

        /**
         * call back to parent 
         */
        this.onListItemSelected?.(selectedItemIndex);
    }

    /**
     * @param innerScale Scale applied to padding, spacing, btnBack
     */
    public SetInnerScale(innerScale: number): void {
        // console.log("SetInnerScale", innerScale);

        // > title
        super.SetInnerScale(innerScale);

        this.itemSpacing.Set(
            this.itemSpacingBase.width * innerScale,
            this.itemSpacingBase.height * innerScale,
        );
    }

    /**
     * @alike: @see OnResize
     * 
     * NOTE: Content is bottom-right aligned
     * @param size 
     */
    // public override SetSize(size: RectSize): void

    public override SetSize(width: number, height: number): void {
        // console.log("SetSize");

        // > size: size
        // > bg: size
        // > btnBack: position
        // > title: position
        // > cachedOverlayHitArea: size
        // > overlay: size
        // > contentMask: size, updateTransforms, updateInPlace
        // > content: width ONLY!
        super.SetSize(width, height);

        const overlayWidth = width - this.borderPadding.left - this.borderPadding.right;
        // const overlayHeight = height - this.borderPadding.top - this.titlePadding.top - this.title.displayHeight - this.titlePadding.bottom - this.borderPadding.bottom;
        const overlayHeight = height - this.borderPadding.top - this.titlePadding.top - this.btnBack.label.displayHeight * this.btnBack.scaleY - this.titlePadding.bottom - this.borderPadding.bottom;

        // console.log(ScreenManager.GetAspectRatio().toFixed(2));
        const curAspectRatio = ScreenManager.GetAspectRatio();
        let curScaleProps: OptionsLeafPopupImageItemsContentScaleItemProps;
        for (let i = 0; i < this.scaleItemProps.length; i++) {
            const scaleProps = this.scaleItemProps[i];
            if (scaleProps.maxAR != undefined) {
                if (curAspectRatio < scaleProps.maxAR) {
                    curScaleProps = scaleProps;
                    break;
                }
            }
            else {
                // max aspect ratio (max landscape)
                if (curAspectRatio > scaleProps.minAR) {
                    curScaleProps = scaleProps;
                    break;
                }
            }
        }

        const numCols = curScaleProps.itemCols;
        // horizontal space left for single row of items, excluding spacing, padding.
        // NOTE: spacing is already scaled
        const itemsHorizontalSpace = overlayWidth - (numCols - 1) * this.itemSpacing.width;
        this.itemScale = (itemsHorizontalSpace / numCols) / this.itemCellWidth;

        let rowIndex: number;
        this.toggleGroup.GetItems().forEach((item: OptionsLeafPopupImageItem, i) => {
            const colIndex = i % numCols;
            rowIndex = (i / numCols) | 0;
            // console.log(colIndex, rowIndex);
            item.scale = this.itemScale;
            item.setPosition(
                colIndex * (this.itemCellWidth * this.itemScale + this.itemSpacing.width),
                rowIndex * (this.itemCellHeight * this.itemScale + this.itemSpacing.height)
            );
        });

        if (rowIndex != undefined) {
            // content: height ONLY (based on items/content height)
            this.content.GetSize().height = (rowIndex + 1) * (this.itemCellHeight * this.itemScale + this.itemSpacing.height) - this.itemSpacing.height;
        }

        this.contentMask.SetMaskRectSize(overlayWidth, overlayHeight);
        // // update transforms as well, since this container's position might have changed also
        // EDIT: now in update @see this.UpdateMask
        // this.contentMask.UpdateTransforms();
        // this.contentMask.UpdateGraphics();

        // > lastScrollDelta 0 an update scroll
        this.UpdateInPlace();
    }
}